import { Box, Grid, Typography } from '@mui/material'
import { AttachmentS3, IProductWarrantyClaim } from '@cbgms/api'
import { AcceptedFileTypes, DragAndDropFileUploader } from '@carsys/ui'
import { AttachmentItem } from '@cbgms/components/general/AttachmentItem'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { useStyles } from '../styles/form.style'
import { fileTypesToAcceptString } from '@carsys/ui/src/FileUploader/utils'
import { useHumanReadableFilesize } from '@carsys/ui/src/FileUploader/use-human-readable-filesize'
import { useBuildMediaItemUrl } from '../hooks/use-build-media-item-url'

interface AttachmentFieldProps {
  file: AttachmentS3
  index: number
}

const AttachmentField = ({ file, index }: AttachmentFieldProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const formatter = useHumanReadableFilesize()
  const buildMediaItemUrl = useBuildMediaItemUrl()
  const { values: formValues, setValues } = useFormikContext<IProductWarrantyClaim>()
  const addFile = (fileList: File[]) => {
    const wholesalerAttachments = [...(formValues?.AttachmentsS3Internal || [])]
    wholesalerAttachments[index] = { ...wholesalerAttachments[index], File: fileList[0], Name: fileList[0]?.name }
    setValues({ ...formValues, ...{ AttachmentsS3Internal: wholesalerAttachments } })
  }
  const maximumFilesize = 10
  const accept = ['jpg', 'jpeg', 'png', 'svg', 'docx', 'pdf', 'xlsx']

  const viewFile = async (fileToView: string) => {
    const wholesalerAttachments = [...(formValues?.AttachmentsS3Internal || [])]
    const fileDetails = wholesalerAttachments?.filter(item => item.Name === fileToView)[0]?.File
    if (fileDetails instanceof File) {
      window.open(URL.createObjectURL(fileDetails as unknown as Blob | MediaSource), '_blank')
    } else {
      window.open(await buildMediaItemUrl(fileToView, formValues?.CompanyUUID || ''), '_blank')
    }
  }

  const removeFile = () => {
    const wholesalerAttachments = [...(formValues?.AttachmentsS3Internal || [])]
    wholesalerAttachments[index] = { Key: '', Name: '', ContentType: '' }
    setValues({ ...formValues, ...{ AttachmentsS3Internal: wholesalerAttachments } })
  }

  const text1 = (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: '4px' }}>
      {t('productWarrantyClaim.fileUploader.dragAndDrop')}&nbsp;
      <Typography variant='body1' sx={{ color: 'rgb(0, 85, 150)' }}>
        {t('productWarrantyClaim.fileUploader.upload').toLocaleUpperCase()}
      </Typography>
      &nbsp;
      {t('productWarrantyClaim.fileUploader.dragAndDropStatement')}
    </Box>
  )
  const text2 = (
    <>
      {t('productWarrantyClaim.fileUploader.acceptedFormats')} {fileTypesToAcceptString(accept as AcceptedFileTypes[])}.&nbsp;&nbsp;
      {t('productWarrantyClaim.fileUploader.maxSize')}
      &nbsp;
      {formatter(maximumFilesize)}
    </>
  )

  return (
    <Grid item sx={{ mt: 2, width: '55%' }} className={classes.noBreakPrintOnly}>
      <Grid xs item>
        <Typography className={classes.attachmentText}>
          {`${t('productWarrantyClaim.attachment')} ${index + 1} (${t('productWarrantyClaim.attachment_internalUseOnly')})`}
        </Typography>
      </Grid>
      {!file.Name ? (
        <DragAndDropFileUploader
          multiple={false}
          maximumFilesize={10}
          onChange={addFile}
          accept={['jpg', 'jpeg', 'png', 'svg', 'docx', 'pdf', 'xlsx']}
          bodyText={text1}
          secondaryText={text2}
        />
      ) : null}
      {file?.Name ? (
        <Grid container spacing={1} sx={{ mt: 1 }}>
          <Grid sx={{ display: 'inline-flex' }} key={index} item data-testid='uploaded-file-item'>
            <AttachmentItem
              default={false}
              url={file.Name}
              view={false}
              handleClick={viewFile}
              handleCancelClick={removeFile}
              marginbottom={0}
              allowFileRemoval
            />
          </Grid>
        </Grid>
      ) : null}
    </Grid>
  )
}

export default AttachmentField
